import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@repo/common/utils/cn";

const alertVariants = cva(
  "relative w-full flex flex-col gap-1 rounded-lg border p-3 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "bg-status-error-lighten-3 border-status-error-lighten-2 [&>svg]:text-status-destructive text-status-error-darken-2",
        warn: "bg-status-warning-lighten-3 border-status-warning-lighten-2 text-foreground [&>svg]:text-status-warning",
        info: "bg-status-info-lighten-3 border-status-info-lighten-2 text-status-info-darken-2",
        success:
          "bg-status-success-lighten-3 border-status-success-lighten-2 text-status-success-darken-2 [&>svg]:text-status-success",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-foreground-muted col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription };
